import { Ionicons } from '@expo/vector-icons';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

type Props = {
  step: number;       // 1-based
  totalSteps: number;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onClose?: () => void;
};

export function WizardLayout({ step, totalSteps, title, subtitle, onBack, onClose }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const s = makeStyles(colors);

  return (
    <View style={s.header}>
      <View style={s.topRow}>
        {onBack ? (
          <Pressable onPress={onBack} hitSlop={10} accessibilityRole="button" style={s.backBtn}>
            <Ionicons name="chevron-back" size={20} color={colors.primary} />
            <Text style={s.backText}>{t('common.back')}</Text>
          </Pressable>
        ) : <View style={s.backBtn} />}
        <Text style={s.stepIndicator}>{t('common.stepOf', { step, total: totalSteps })}</Text>
        {onClose ? (
          <Pressable onPress={onClose} hitSlop={10} accessibilityRole="button" style={s.closeBtn}>
            <Ionicons name="close" size={20} color={colors.textMuted} />
          </Pressable>
        ) : <View style={s.closeBtn} />}
      </View>

      {/* Progress bar */}
      <View style={s.progressTrack}>
        <View style={[s.progressFill, { width: `${(step / totalSteps) * 100}%` as any }]} />
      </View>

      <Text style={s.title}>{title}</Text>
      {!!subtitle && <Text style={s.subtitle}>{subtitle}</Text>}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    header: {
      marginBottom: spacing.lg,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.sm,
    },
    backBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 60,
    },
    backText: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    closeBtn: {
      minWidth: 30,
      alignItems: 'flex-end',
    },
    stepIndicator: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      flexShrink: 1,
      textAlign: 'center',
    },
    progressTrack: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
      marginBottom: spacing.md,
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: 2,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '700',
      marginBottom: 4,
    },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
  });
}
